// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import RestaurantMap from './RestaurantMap';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { Video } from '../types';
import { cleanUrl } from '../utils/url';
import { X } from 'lucide-react';
import { searchPlacesServer, getPlaceDetailsServer } from '../services/placesSearch';
import StarRating from './StarRating';
import MediaPreview from './MediaPreview';

type Props = { video: Video | null; isOpen: boolean; onClose: () => void };

// Helpers: NA checks, HTML entity decode, and image proxy for FB/IG thumbs
const isNA = (v?: any) => !v || v === 'N/A';

function decodeEntities(s = '') {
  if (!s) return '';
  const d = document.createElement('textarea');
  d.innerHTML = s;
  return d.value;
}

// Removed proxifyImage - now handled by MediaPreview component

// Removed preferImageUrl - now handled by MediaPreview component

function ensureInstagram() {
  if (!document.getElementById('ig-embed-js')) {
    const s = document.createElement('script');
    s.id = 'ig-embed-js';
    s.async = true;
    s.src = 'https://www.instagram.com/embed.js';
    document.body.appendChild(s);
  } else {
    (window as any).instgrm?.Embeds?.process();
  }
}
function ensureFacebook() {
  if (!document.getElementById('fb-root')) {
    const r = document.createElement('div');
    r.id = 'fb-root';
    document.body.appendChild(r);
  }
  if (!document.getElementById('fb-sdk')) {
    const s = document.createElement('script');
    s.id = 'fb-sdk';
    s.async = true;
    s.crossOrigin = 'anonymous';
    s.src = 'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v21.0';
    document.body.appendChild(s);
  } else {
    (window as any).FB?.XFBML?.parse();
  }
}

function ensureTikTok() {
  if (!document.getElementById('tt-embed-js')) {
    const s = document.createElement('script');
    s.id = 'tt-embed-js';
    s.async = true;
    s.src = 'https://www.tiktok.com/embed.js';
    document.body.appendChild(s);
  }
}

// Minimal sanitizer for provider/Places attribution HTML
function sanitizeAttribution(html: string) {
  try {
    const t = document.createElement('template');
    t.innerHTML = html;
    t.content.querySelectorAll('*').forEach((el: any) => {
      [...el.attributes].forEach((a: any) => {
        if (/^on/i.test(a.name)) el.removeAttribute(a.name);
      });
      if (el.tagName === 'SCRIPT') el.remove();
      if (el.tagName === 'A') {
        el.setAttribute('rel', 'noopener noreferrer');
        el.setAttribute('target', '_blank');
      }
    });
    return t.innerHTML;
  } catch {
    return '';
  }
}

const VideoModal: React.FC<Props> = ({ video, isOpen, onClose }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [enrichedData, setEnrichedData] = useState<any>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const [showReviews, setShowReviews] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false); // disabled by default to avoid layout issues
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    setShowReviews(false);
    setEnrichedData(null); // Reset enriched data when video changes
  }, [video, isOpen]);

  // ✅ Modal enrichment REMOVED to save API costs!
  // Background enrichment in App.tsx handles everything automatically
  useEffect(() => {
    if (!video || !isOpen) return;
    setEnrichedData(null); // Clear previous state
    console.log('Modal opened - no API calls, using background-enriched data only');
  }, [video?.id, isOpen]);

  // Call IG/FB processors after embed HTML is in DOM (next macrotask)
  useEffect(() => {
    if (!isOpen || !video || !showEmbed) return;
    const t = setTimeout(() => {
      if (video.platform === 'Instagram') ensureInstagram();
      if (video.platform === 'Facebook') ensureFacebook();
  if (video.platform === 'TikTok') ensureTikTok();
    }, 0);
    return () => clearTimeout(t);
  }, [isOpen, video?.platform, video?.embed_html, showEmbed]);

  // Re-process provider embeds after paint and on DOM mutations for reliability
  useEffect(() => {
    if (!isOpen || !showEmbed || !video?.embed_html) return;
    const run = () => {
      if (video.platform === 'Instagram') (window as any).instgrm?.Embeds?.process();
      if (video.platform === 'Facebook') (window as any).FB?.XFBML?.parse();
  if (video.platform === 'TikTok') {/* embed.js auto-discovers; ensure present */ ensureTikTok(); }
    };
    const t = setTimeout(run, 0);
    const mo = new MutationObserver(run);
    if (containerRef.current) mo.observe(containerRef.current, { childList: true, subtree: true });
    return () => { clearTimeout(t); mo.disconnect(); };
  }, [isOpen, video?.embed_html, video?.platform, showEmbed]);

  // Lock body scroll, focus trap, and close on Escape while modal open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Save and move focus inside dialog
    lastFocusedRef.current = (document.activeElement as HTMLElement) || null;
    const focusFirst = () => {
      const root = dialogRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusables[0] || root;
      (first as HTMLElement).focus();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
      if (e.key === 'Tab') {
        const root = dialogRef.current;
        if (!root) return;
        const focusables = Array.from(
          root.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])')
        ).filter(el => el.offsetParent !== null);
        if (focusables.length === 0) {
          e.preventDefault();
          (root as HTMLElement).focus();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const current = document.activeElement as HTMLElement;
        if (!e.shiftKey && current === last) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && current === first) {
          e.preventDefault();
          last.focus();
        }
      }
    };
    // Defer focus to ensure dialog is in the DOM
    const ft = setTimeout(focusFirst, 0);
  window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
      clearTimeout(ft);
      // Restore focus
      const prevEl = lastFocusedRef.current;
      if (prevEl && typeof prevEl.focus === 'function') {
        setTimeout(() => prevEl.focus(), 0);
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen || !video) return null;

  // MediaPreview handles its own aspect ratio, no need for mediaCls

  const baseTitle = decodeEntities(
    video.title && video.title !== 'N/A' ? video.title : (video.platform ? `${video.platform} Video` : 'Video')
  );
  const title = (video.restaurant_name && video.restaurant_name !== 'N/A') ? video.restaurant_name : baseTitle;
  const creator = video.creator_name && video.creator_name !== 'N/A' ? video.creator_name : '';
  const hasRestaurantInfo = Boolean(
    (!isNA(video.restaurant_name)) ||
    (!isNA(video.restaurant_location)) ||
    (!isNA(video.restaurant_address)) ||
    (!isNA(video.restaurant_cuisine)) ||
    (typeof video.restaurant_rating === 'number' && video.restaurant_rating > 0) ||
    (!isNA(video.restaurant_price_range)) ||
    (!isNA(video.extracted_dish_name)) ||
    (Array.isArray(video.extracted_ingredients) && video.extracted_ingredients.length) ||
    (typeof video.extracted_estimated_price === 'number' && video.extracted_estimated_price > 0)
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm overflow-y-auto overscroll-contain p-4 md:p-6 flex items-start justify-center"
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduceMotion ? { opacity: 1 } : { opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
          key={video.id || video.video_url}
            className="mx-auto w-full max-w-3xl bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
            initial={reduceMotion ? false : { y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={reduceMotion ? { opacity: 1 } : { y: 20, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={title || 'Video'}
            aria-live="polite"
            tabIndex={-1}
            ref={dialogRef}
          >
            <button
              onClick={onClose}
              className="absolute top-3 right-3 z-10 rounded-full bg-black/60 text-white p-2 hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <MediaPreview
              provider={video.platform || 'Unknown'}
              url={video.video_url}
              thumbnailUrl={(() => {
                const thumbnail = enrichedData?.places_photo_url ||
                                 (video as any).places_photo_url ||
                                 (video as any).provider_thumbnail_url ||
                                 video.thumbnail_url;
                // Debug logs removed for production
                return thumbnail;
              })()}
              title={title}
            />
            {/* Photo attribution (required by Google Places terms) */}
            {video.photo_attribution_html && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[11px] px-2 py-1 leading-tight z-10">
                <div dangerouslySetInnerHTML={{ __html: sanitizeAttribution(video.photo_attribution_html) }} />
              </div>
            )}
            <div className="sticky top-0 bg-white/90 backdrop-blur px-4 py-3 border-b flex items-center justify-between z-10 flex-none">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold truncate pr-4">{title}</h3>
                {creator && (
                  <p className="text-sm text-gray-600 truncate">
                    <span className="font-medium">{creator}</span>
                    {video.platform ? (
                      <>
                        {' '}en <span className="font-medium">{video.platform}</span>
                      </>
                    ) : null}
                  </p>
                )}
              </div>
              <button onClick={onClose} className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm">Cerrar</button>
            </div>

            {/* Details section */}
            <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-4" style={{ WebkitOverflowScrolling: 'touch' }}>
              {hasRestaurantInfo ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {video.restaurant_name && video.restaurant_name !== 'N/A' && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-gray-800 font-semibold">{video.restaurant_name}</p>
                      {video.restaurant_location && video.restaurant_location !== 'N/A' && (
                        <p className="text-gray-500 text-sm">{video.restaurant_location}</p>
                      )}
                    </div>
                  )}
                  {video.restaurant_address && video.restaurant_address !== 'N/A' && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-gray-700 text-sm">{video.restaurant_address}</p>
                    </div>
                  )}
                  {(enrichedData?.restaurant_cuisine || (video.restaurant_cuisine && video.restaurant_cuisine !== 'N/A')) && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-gray-700 text-sm">Cocina: {enrichedData?.restaurant_cuisine || video.restaurant_cuisine}</p>
                    </div>
                  )}
                  {(() => {
                    const rating = enrichedData?.restaurant_rating ?? video.restaurant_rating;
                    return (typeof rating === 'number' && rating > 0) ? (
                      <div className="bg-gray-50 rounded-xl p-3">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-700 text-sm font-medium">Calificación:</span>
                          <StarRating value={rating} size="sm" showNumber />
                        </div>
                      </div>
                    ) : null;
                  })()}
                  {(enrichedData?.restaurant_price_range || (video.restaurant_price_range && video.restaurant_price_range !== 'N/A')) && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-gray-700 text-sm">Rango de Precios: {enrichedData?.restaurant_price_range || video.restaurant_price_range}</p>
                    </div>
                  )}
                  {(enrichedData?.opening_hours_weekday_text || (video as any).opening_hours_weekday_text) && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <div className="text-gray-700 text-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">Horarios:</span>
                          {typeof enrichedData?.open_now === 'boolean' && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${enrichedData.open_now ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {enrichedData.open_now ? 'Abierto ahora' : 'Cerrado'}
                            </span>
                          )}
                        </div>
                        <div className="text-xs space-y-0.5">
                          {(enrichedData?.opening_hours_weekday_text || (video as any).opening_hours_weekday_text || []).slice(0, 3).map((hour: string, idx: number) => (
                            <div key={idx}>{hour}</div>
                          ))}
                          {(enrichedData?.opening_hours_weekday_text || (video as any).opening_hours_weekday_text || []).length > 3 && (
                            <div className="text-gray-500">...</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {video.extracted_dish_name && video.extracted_dish_name !== 'N/A' && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-gray-700 text-sm">Plato Destacado: {video.extracted_dish_name}</p>
                    </div>
                  )}
                  {Array.isArray(video.extracted_ingredients) && video.extracted_ingredients.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-gray-700 text-sm">Ingredientes: {video.extracted_ingredients.join(', ')}</p>
                    </div>
                  )}
                  {(typeof video.extracted_estimated_price === 'number' && video.extracted_estimated_price > 0) && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-gray-700 text-sm">Precio Estimado: ${video.extracted_estimated_price.toFixed(2)}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 italic">No se encontró información detallada del restaurante en este video.</p>
              )}

              {video.google_maps_url && video.google_maps_url !== 'N/A' && (
                <a
                  href={video.google_maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors"
                >
                  Ver en Google Maps
                </a>
              )}
              {!isNA(video.video_url) && (
                <a
                  href={video.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors ml-2"
                >
                  Ver publicación
                </a>
              )}

              {Array.isArray(video.reviews) && video.reviews.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-3">
                  <button
                    className="mb-2 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-md font-semibold hover:bg-yellow-200 transition-colors text-sm"
                    onClick={() => setShowReviews((v) => !v)}
                  >
                    {showReviews ? 'Ocultar Reseñas' : 'Ver Reseñas'}
                  </button>
                  <div className="text-[11px] text-gray-400">Opiniones y fotos pueden provenir de Google. © Google</div>
                  {showReviews && (
                    <ul className="space-y-3 max-h-60 overflow-y-auto pr-2">
                      {video.reviews.map((r, i) => (
                        <li key={i} className="border-b last:border-b-0 pb-2">
                          <div className="flex items-center gap-2 mb-1">
                            {r.profile_photo_url && (
                              <img src={r.profile_photo_url} alt={r.author_name} className="w-6 h-6 rounded-full border" />
                            )}
                            <span className="font-semibold text-gray-800 text-sm">{r.author_name}</span>
                            {(typeof r.rating === 'number' && r.rating > 0) && (
                              <span className="text-yellow-600 font-bold text-sm">{r.rating}★</span>
                            )}
                            {r.relative_time && <span className="text-gray-400 text-xs">{r.relative_time}</span>}
                          </div>
                          <div className="text-gray-700 text-sm">{r.text}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Inline map to mirror previous modal info */}
              <RestaurantMap
                address={video.restaurant_address !== 'N/A' ? video.restaurant_address : undefined}
                location={video.restaurant_location !== 'N/A' ? video.restaurant_location : undefined}
                restaurantName={video.restaurant_name !== 'N/A' ? video.restaurant_name : undefined}
              />
              <div className="text-[11px] text-gray-400">Mapa y datos de lugares de Google. © Google</div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VideoModal;